<template>
  <PageLayout title="塔罗牌">

    <!-- ==================== 移动端独立模板 ==================== -->
    <div v-if="isMobile" class="mobile-tarot">
      <!-- 紧凑头部 -->
      <div class="mobile-header">
        <div class="mobile-header-icon">🎴</div>
        <div class="mobile-header-text">
          <h2>塔罗占卜</h2>
          <p>"每一张牌都是生命的启示"</p>
        </div>
      </div>

      <!-- 准备引导 - 可折叠Banner -->
      <div v-if="currentPhase === 'prepare'" class="mobile-prepare-banner" ref="mobilePrepareRef">
        <div class="mobile-banner-card" :class="{ 'mobile-banner-collapsed': mobileBannerCollapsed }">
          <div class="mobile-banner-header" @click="mobileBannerCollapsed = !mobileBannerCollapsed">
            <span class="mobile-banner-title">🌙 占卜仪式</span>
            <span class="mobile-banner-toggle">{{ mobileBannerCollapsed ? '▸' : '▾' }}</span>
          </div>
          <div class="mobile-banner-body" v-show="!mobileBannerCollapsed">
            <div class="mobile-banner-step">
              <span class="mobile-step-num">1</span>
              <span>找一个安静的空间，深呼吸，让心绪平静</span>
            </div>
            <div class="mobile-banner-step">
              <span class="mobile-step-num">2</span>
              <span>在心中默念您想要询问的问题</span>
            </div>
            <div class="mobile-banner-step">
              <span class="mobile-step-num">3</span>
              <span>根据问题复杂度选择合适牌阵</span>
            </div>
          </div>
        </div>
        <button class="mobile-action-btn mobile-start-btn" @click="startReading">
          开始占卜 ✨
        </button>
      </div>

      <!-- 占卜阶段 -->
      <div v-if="currentPhase === 'reading'" class="mobile-reading">

        <!-- 洗牌动画（全屏遮罩） -->
        <div v-if="isShuffling" class="mobile-shuffle-overlay">
          <div class="mobile-shuffle-animation">
            <div class="mobile-shuffle-cards">
              <div v-for="i in 5" :key="i" class="mobile-shuffle-card" :style="getShuffleStyle(i)"></div>
            </div>
            <p class="mobile-shuffle-text">洗牌中... 请专注您的问题 ✨</p>
          </div>
        </div>

        <!-- 问题+牌阵 合并卡片 -->
        <div v-if="drawnCards.length === 0 && !result" class="mobile-card" ref="mobileQuestionRef">
          <div class="mobile-card-title">💫 请输入您的问题</div>
          <textarea
            v-model="formData.question"
            class="mobile-textarea"
            rows="3"
            placeholder="请清晰描述您想占卜的问题，例如：我最近的事业发展如何？"
            :disabled="isDrawing"
          ></textarea>

          <div class="mobile-spread-label">🔮 选择牌阵</div>
          <div class="mobile-spread-chips">
            <div
              v-for="(config, key) in spreadConfigs"
              :key="key"
              class="mobile-spread-chip"
              :class="{ 'mobile-chip-active': formData.spread === key, 'mobile-chip-disabled': isDrawing }"
              @click="!isDrawing && (formData.spread = key)"
            >
              <span class="mobile-chip-icon">{{ getSpreadIcon(key) }}</span>
              <span class="mobile-chip-name">{{ config.name }}</span>
              <span class="mobile-chip-count">{{ config.cardCount }}张</span>
            </div>
          </div>

          <button
            class="mobile-action-btn mobile-draw-btn"
            :disabled="!formData.question.trim() || isDrawing"
            @click="drawCards"
          >
            <span v-if="!isDrawing">🎴 抽取塔罗牌</span>
            <span v-else>正在抽牌...</span>
          </button>
        </div>

        <!-- 牌面展示 - 横向滑动 (解读结果出来后保留, 与 PC 端行为一致, 方便对照牌面) -->
        <div v-if="drawnCards.length > 0" class="mobile-cards-section" :class="{ 'mobile-cards-in-result': !!result }" ref="mobileCardsRef">
          <div class="mobile-section-title">✨ 您的牌面</div>
          <div class="mobile-cards-scroll">
            <div v-for="(card, index) in drawnCards" :key="index"
                 class="mobile-tarot-card"
                 :class="{
                   'mobile-card-reversed': card.reversed,
                   'mobile-card-flipped': flippedCards[index],
                   'mobile-card-animate': showCards
                 }"
                 :style="{ animationDelay: `${index * 0.15}s` }"
                 @click="flipCard(index)">
              <!-- 牌背 -->
              <div class="mobile-card-back" v-if="!flippedCards[index]">
                <img :src="backImage" alt="牌背" class="mobile-back-image" />
                <div class="mobile-tap-hint">点击翻开</div>
              </div>
              <!-- 牌面 -->
              <div class="mobile-card-front" v-else>
                <div class="mobile-card-img-wrap">
                  <img :src="getCardImage(card)" :alt="card.name" class="mobile-card-img" :class="{ 'mobile-img-reversed': card.reversed }" />
                  <div v-if="card.reversed" class="mobile-reversed-badge">逆位</div>
                </div>
                <div class="mobile-card-meta">
                  <span class="mobile-card-position">{{ card.position }}</span>
                  <span class="mobile-card-name">{{ card.name }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- 翻开所有牌 大按钮 -->
          <button v-if="!allCardsFlipped" class="mobile-action-btn mobile-flip-all-btn" @click="flipAllCards">
            翻开所有牌 🔄
          </button>
        </div>

        <!-- 解读按钮 -->
        <div v-if="allCardsFlipped && !result" class="mobile-action-section" ref="mobileInterpretRef">
          <button class="mobile-action-btn mobile-interpret-btn" :disabled="isInterpreting" @click="getInterpretation">
            <span v-if="!isInterpreting">🔮 开始解读</span>
            <span v-else>正在解读中...</span>
          </button>
          <p v-if="isInterpreting" class="mobile-loading-fun-msg">{{ currentLoadingMsg }}</p>
        </div>

        <!-- AI解读结果 -->
        <div v-if="result" class="mobile-result-section" ref="mobileResultRef">
          <div class="mobile-card mobile-full-width">
            <div class="mobile-card-title">
              <span>🔮 塔罗解读</span>
              <span class="mobile-spread-badge">{{ drawnCards[0]?.spread?.name || '牌阵解读' }}</span>
            </div>
            <div class="mobile-interpretation" v-html="formattedResult"></div>
          </div>

          <button class="mobile-action-btn mobile-restart-btn" @click="resetReading">
            🔄 重新占卜
          </button>
          <button class="mobile-action-btn fh-share-btn fh-share-btn--block" @click="shareVisible = true" style="margin-top:10px">
            📣 分享我的占卜
          </button>
        </div>

      </div>
    </div>

    <!-- ==================== PC端模板（完整保留） ==================== -->
    <div v-else>
      <div class="page-header">
        <div class="header-icon">🎴</div>
        <div class="header-text">
          <h2 class="subtitle">探索命运指引</h2>
          <p class="motto">"每一张牌都是生命的启示"</p>
        </div>
      </div>

      <!-- 准备阶段引导 -->
      <div v-if="currentPhase === 'prepare'" class="prepare-section">
        <MysticalCard class="guide-card">
          <div class="guide-content">
            <h3>🌙 塔罗占卜仪式</h3>
            <div class="guide-steps">
              <div class="guide-step">
                <span class="step-number">1</span>
                <div class="step-content">
                  <h4>静心准备</h4>
                  <p>找一个安静的空间，深呼吸，让心绪平静下来。</p>
                </div>
              </div>
              <div class="guide-step">
                <span class="step-number">2</span>
                <div class="step-content">
                  <h4>明确问题</h4>
                  <p>在心中默念您想要询问的问题，保持专注。</p>
                </div>
              </div>
              <div class="guide-step">
                <span class="step-number">3</span>
                <div class="step-content">
                  <h4>选择牌阵</h4>
                  <p>根据问题的复杂程度，选择合适的牌阵。</p>
                </div>
              </div>
            </div>
            <el-button type="primary" size="large" @click="startReading" class="start-btn">
              开始占卜 ✨
            </el-button>
          </div>
        </MysticalCard>
      </div>

      <!-- 占卜阶段 -->
      <div v-else class="reading-section">
        <!-- 问题输入 -->
        <MysticalCard class="input-section">
          <template #header>
            <div class="card-header">
              <span>💫 请输入您的问题</span>
            </div>
          </template>
          <el-form :model="formData" label-position="top">
            <el-form-item label="占卜问题">
              <el-input
                v-model="formData.question"
                type="textarea"
                :rows="3"
                placeholder="请清晰描述您想要占卜的问题，例如：我最近的事业发展如何？这段感情会有结果吗？"
                :disabled="isDrawing" />
            </el-form-item>
          </el-form>
        </MysticalCard>

        <!-- 牌阵选择 -->
        <MysticalCard class="spread-section">
          <template #header>
            <div class="card-header">
              <span>🔮 选择牌阵</span>
            </div>
          </template>
          <div class="spread-grid">
            <div
              v-for="(config, key) in spreadConfigs"
              :key="key"
              class="spread-option"
              :class="{ active: formData.spread === key, disabled: isDrawing }"
              @click="!isDrawing && (formData.spread = key)">
              <div class="spread-icon">{{ getSpreadIcon(key) }}</div>
              <div class="spread-info">
                <h4>{{ config.name }}</h4>
                <p>{{ config.description }}</p>
                <span class="card-count">{{ config.cardCount }}张牌</span>
              </div>
            </div>
          </div>
        </MysticalCard>

        <!-- 抽牌按钮 -->
        <div class="action-section">
          <el-button
            type="primary"
            size="large"
            @click="drawCards"
            :loading="isDrawing"
            :disabled="!formData.question.trim()"
            class="draw-btn">
            <span v-if="!isDrawing">🎴 抽取塔罗牌</span>
            <span v-else>正在抽牌...</span>
          </el-button>
        </div>

        <!-- 洗牌动画 -->
        <div v-if="isShuffling" class="shuffle-overlay">
          <div class="shuffle-animation">
            <div class="shuffle-cards">
              <div v-for="i in 5" :key="i" class="shuffle-card" :style="getShuffleStyle(i)"></div>
            </div>
            <p class="shuffle-text">洗牌中... 请专注您的问题 ✨</p>
          </div>
        </div>

        <!-- 牌面展示区域 -->
        <div v-if="drawnCards.length > 0" class="cards-section">
          <h3 class="section-title">✨ 您的牌面</h3>
          <div class="cards-container" :class="`spread-${formData.spread}`">
            <div v-for="(card, index) in drawnCards" :key="index"
                 class="tarot-card"
                 :class="{
                   'card-reversed': card.reversed,
                   'card-flipped': flippedCards[index],
                   'card-animate': showCards
                 }"
                 :style="{ animationDelay: `${index * 0.15}s` }"
                 @click="flipCard(index)">
              <!-- 牌背（初始状态） -->
              <div class="card-back" v-if="!flippedCards[index]">
                <img :src="backImage" alt="牌背" class="back-image" />
                <div class="tap-hint">点击翻开</div>
              </div>
              <!-- 牌面 -->
              <div class="card-front" v-else>
                <div class="card-image-wrapper">
                  <img :src="getCardImage(card)" :alt="card.name" class="card-image" :class="{ 'reversed-img': card.reversed }" />
                  <div v-if="card.reversed" class="reversed-badge">逆位</div>
                </div>
                <div class="card-info">
                  <span class="card-position">{{ card.position }}</span>
                  <span class="card-name">{{ card.name }}</span>
                  <span class="card-meaning">{{ card.meaning }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- 一键翻牌按钮 -->
          <div v-if="!allCardsFlipped" class="flip-all-btn">
            <el-button @click="flipAllCards" type="warning" plain>
              翻开所有牌
            </el-button>
          </div>
        </div>

        <!-- 解读按钮 -->
        <div v-if="allCardsFlipped && !result" class="interpret-btn-section">
          <el-button type="primary" size="large" @click="getInterpretation" :loading="isInterpreting">
            <span v-if="!isInterpreting">🔮 开始解读</span>
            <span v-else>正在解读中...</span>
          </el-button>
          <p v-if="isInterpreting" class="loading-fun-msg">{{ currentLoadingMsg }}</p>
        </div>

        <!-- 结果展示区 -->
        <div v-if="result" class="result-section">
          <MysticalCard>
            <template #header>
              <div class="card-header interpretation-header">
                <span>🔮 塔罗解读</span>
                <span class="spread-name">{{ drawnCards[0]?.spread?.name || '牌阵解读' }}</span>
              </div>
            </template>
            <div class="interpretation" v-html="formattedResult"></div>
          </MysticalCard>
        </div>

        <!-- 重新占卜按钮 -->
        <div v-if="result" class="restart-section">
          <el-button @click="resetReading" size="large">
            🔄 重新占卜
          </el-button>
          <el-button type="primary" size="large" class="draw-btn" @click="shareVisible = true" style="margin-left:12px">
            📣 分享我的占卜
          </el-button>
        </div>
      </div>
    </div>

    <TarotShareCard
      v-model:visible="shareVisible"
      :card="shareCardData"
      :reversed="!!shareMainCard?.reversed"
      :image="shareImage"
      :extra-cards="shareExtraCards"
      :hero-position="shareMainCard?.position || ''"
      :spread-name="shareSpreadName"
      page="tarot"
    />
    <PayDialog
      v-model:visible="showPayDialog"
      :cost="payDialogCost"
      :balance="payDialogBalance"
      :feature="payDialogFeature"
      :rmb-price="payDialogRmbPrice"
      @goPoints="goToPoints"
    />
  </PageLayout>
</template>

<script setup>
import { ref, reactive, computed, nextTick, watch } from 'vue'
import { ElMessage } from 'element-plus'
import { marked } from 'marked'
import PageLayout from '@/components/layout/PageLayout.vue'
import { MysticalCard } from '@/components/common'
import { TarotShareCard } from '@/components/share'
import { majorArcana } from '@/data/tarot/majorArcana'
import { minorArcanaComplete } from '@/data/tarot/minorArcana'
import { analyzeTarotReading, spreadConfigs, drawTarotCards } from '@/api/tarot'
import { API_CONFIG } from '@/config'
import { useMobile } from '@/composables/useMobile'
import { useAuthGuard } from '@/composables/useAuthGuard'
import { getLoadingMessages } from '@/data/loadingMessages'
import PayDialog from '@/components/common/PayDialog.vue'
import { usePaymentGuard } from '@/composables/usePaymentGuard'

// 移动端检测
const { isMobile } = useMobile()
const { requireLogin } = useAuthGuard()
const { showPayDialog, payDialogCost, payDialogBalance, payDialogFeature, payDialogRmbPrice, checkFeatureCost, goToPoints } = usePaymentGuard()

// 动态导入所有塔罗牌图片（WebP 双档：PC 用 desktop 512px，移动用 mobile 384px；原图 PNG 保留在 tarot/ 供小程序等复用）
const majorImagesDesktop = import.meta.glob('@/assets/tarot-webp/desktop/major/*.webp', { eager: true, as: 'url' })
const wandsImagesDesktop = import.meta.glob('@/assets/tarot-webp/desktop/minor/wands/*.webp', { eager: true, as: 'url' })
const cupsImagesDesktop = import.meta.glob('@/assets/tarot-webp/desktop/minor/cups/*.webp', { eager: true, as: 'url' })
const swordsImagesDesktop = import.meta.glob('@/assets/tarot-webp/desktop/minor/swords/*.webp', { eager: true, as: 'url' })
const pentaclesImagesDesktop = import.meta.glob('@/assets/tarot-webp/desktop/minor/pentacles/*.webp', { eager: true, as: 'url' })
const majorImagesMobile = import.meta.glob('@/assets/tarot-webp/mobile/major/*.webp', { eager: true, as: 'url' })
const wandsImagesMobile = import.meta.glob('@/assets/tarot-webp/mobile/minor/wands/*.webp', { eager: true, as: 'url' })
const cupsImagesMobile = import.meta.glob('@/assets/tarot-webp/mobile/minor/cups/*.webp', { eager: true, as: 'url' })
const swordsImagesMobile = import.meta.glob('@/assets/tarot-webp/mobile/minor/swords/*.webp', { eager: true, as: 'url' })
const pentaclesImagesMobile = import.meta.glob('@/assets/tarot-webp/mobile/minor/pentacles/*.webp', { eager: true, as: 'url' })
const backImageDesktop = new URL('@/assets/tarot-webp/desktop/back.webp', import.meta.url).href
const backImageMobile = new URL('@/assets/tarot-webp/mobile/back.webp', import.meta.url).href
const backImage = computed(() => (isMobile.value ? backImageMobile : backImageDesktop))

// 获取牌面图片
const getCardImage = (card) => {
  const isMobileTier = isMobile.value
  let images, pattern

  switch (card.suit) {
    case 'major':
      images = isMobileTier ? majorImagesMobile : majorImagesDesktop
      pattern = `/major/${card.number}.webp`
      break
    case 'wands':
      images = isMobileTier ? wandsImagesMobile : wandsImagesDesktop
      pattern = `/minor/wands/${card.number <= 10 ? card.number : ['page', 'knight', 'queen', 'king'][card.number - 11]}.webp`
      break
    case 'cups':
      images = isMobileTier ? cupsImagesMobile : cupsImagesDesktop
      pattern = `/minor/cups/${card.number <= 10 ? card.number : ['page', 'knight', 'queen', 'king'][card.number - 11]}.webp`
      break
    case 'swords':
      images = isMobileTier ? swordsImagesMobile : swordsImagesDesktop
      pattern = `/minor/swords/${card.number <= 10 ? card.number : ['page', 'knight', 'queen', 'king'][card.number - 11]}.webp`
      break
    case 'pentacles':
      images = isMobileTier ? pentaclesImagesMobile : pentaclesImagesDesktop
      pattern = `/minor/pentacles/${card.number <= 10 ? card.number : ['page', 'knight', 'queen', 'king'][card.number - 11]}.webp`
      break
    default:
      return backImage.value
  }

  const matchedKey = Object.keys(images).find(key => key.includes(pattern))
  return matchedKey ? images[matchedKey] : backImage.value
}

// 牌阵图标
const getSpreadIcon = (spread) => {
  const icons = {
    single: '💠',
    three: '🔺',
    celtic: '✚',
    quarter: '☀️'
  }
  return icons[spread] || '💠'
}

// 记录交互
const logInteraction = async (type, question, cards, interpretation) => {
  try {
    const token = localStorage.getItem('token')
    if (!token) return
    await fetch(`${API_CONFIG.baseUrl}${API_CONFIG.endpoints.persona.interaction}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ type, question, result: { cards, spread: formData.spread }, interpretation })
    })
  } catch (error) {
    console.error('记录交互失败:', error)
  }
}

// 状态
const currentPhase = ref('prepare') // prepare, reading
const formData = reactive({ question: '', spread: 'single' })
const result = ref('')
const shareVisible = ref(false)
// ——— 增强分享卡(TarotShareCard) props 组装 ———
// 抽到的牌(drawTarotCards 只带 name/number/suit/meaning/reversed) → 回查完整牌义数据(nameEn/element/keywords/upright/reversed/astrology)
const enrichTarotCard = (c) => {
  if (!c) return null
  if (c.suit === 'major') {
    const m = majorArcana.find(x => x.id === c.number || x.name === c.name)
    return m ? { ...m } : { ...c }
  }
  const suitData = minorArcanaComplete[c.suit]
  const m = suitData?.cards.find(x => x.id === c.number || x.name === c.name)
  // 小阿卡纳 element 在牌组层, 补到牌对象上
  return m ? { ...m, element: m.element || suitData.element } : { ...c }
}
// 牌阵主牌: 各牌阵的叙事主角(配置 heroIndex 驱动)——单张/凯尔特(当前处境)/四季(核心指引)取首张,
// 三张牌阵(过去/现在/未来)取「现在」; 分享卡 hero 与缩略图位次都由它派生
const shareHeroIndex = computed(() => spreadConfigs[formData.spread]?.heroIndex ?? 0)
const shareMainCard = computed(() => drawnCards.value[shareHeroIndex.value] || null)
const shareCardData = computed(() => enrichTarotCard(shareMainCard.value))
const shareImage = computed(() => {
  const c = drawnCards.value[shareHeroIndex.value]
  return c ? getCardImage(c) : ''
})
// 同阵其余牌缩略(排除主牌, 全量传给分享卡——四季牌阵4张缩略全显; ⚠️ 方位字段名是 reversed 不是 isReversed)
const shareExtraCards = computed(() => drawnCards.value
  .map((c, i) => ({ c, i }))
  .filter(({ i }) => i !== shareHeroIndex.value)
  .map(({ c }) => ({ name: c.name, reversed: !!c.reversed, image: getCardImage(c), position: c.position || '' })))
const shareSpreadName = computed(() => spreadConfigs[formData.spread]?.name || '')
const drawnCards = ref([])
const isDrawing = ref(false)
const isShuffling = ref(false)
const isInterpreting = ref(false)
const showCards = ref(false)

// 加载文案轮播
const loadingMsgs = ref([])
const currentLoadingMsg = ref('')
let loadingMsgTimer = null
function startLoadingMessages() {
  loadingMsgs.value = getLoadingMessages('tarot', 8)
  currentLoadingMsg.value = loadingMsgs.value[0]
  let idx = 0
  loadingMsgTimer = setInterval(() => {
    idx = (idx + 1) % loadingMsgs.value.length
    currentLoadingMsg.value = loadingMsgs.value[idx]
  }, 2500)
}
function stopLoadingMessages() {
  if (loadingMsgTimer) { clearInterval(loadingMsgTimer); loadingMsgTimer = null }
}
const flippedCards = ref({})

// 移动端专用状态
const mobileBannerCollapsed = ref(false)

// 移动端自动滚动 refs
const mobileQuestionRef = ref(null)
const mobileCardsRef = ref(null)
const mobileInterpretRef = ref(null)
const mobileResultRef = ref(null)

// 计算属性
const formattedResult = computed(() => result.value ? marked(result.value) : '')
const allCardsFlipped = computed(() => {
  if (drawnCards.value.length === 0) return false
  return drawnCards.value.every((_, i) => flippedCards.value[i])
})

// 方法
const startReading = () => {
  currentPhase.value = 'reading'
}

const drawCards = async () => {
  if (!formData.question.trim()) {
    ElMessage.warning('请输入占卜问题')
    return
  }

  try {
    // 洗牌动画
    isShuffling.value = true
    await new Promise(resolve => setTimeout(resolve, 2000))
    isShuffling.value = false

    isDrawing.value = true
    drawnCards.value = []
    showCards.value = false
    result.value = ''
    flippedCards.value = {}

    // 抽牌（不调用AI）
    drawnCards.value = drawTarotCards(formData.spread)

    await nextTick()
    setTimeout(() => { showCards.value = true }, 100)

  } catch (error) {
    console.error('抽牌失败:', error)
    ElMessage.error('抽牌失败，请稍后重试')
  } finally {
    isDrawing.value = false
  }
}

const flipCard = (index) => {
  if (!showCards.value) return
  flippedCards.value[index] = true
}

const flipAllCards = () => {
  drawnCards.value.forEach((_, i) => {
    setTimeout(() => {
      flippedCards.value[i] = true
    }, i * 150)
  })
}

const getInterpretation = async () => {
  if (!requireLogin('tarot-ai')) return
  try {
    // 积分守卫：动态查价（feature-cost，含会员折扣/免费额度），查价失败放行由后端扣费兜底
    const canProceed = await checkFeatureCost('tarot', '塔罗解读')
    if (!canProceed) return

    isInterpreting.value = true
    startLoadingMessages()

    // 传递已抽好的牌，避免重新抽牌
    const response = await analyzeTarotReading({
      question: formData.question,
      spread: formData.spread,
      cards: drawnCards.value
    })

    result.value = response.data

    await logInteraction('tarot', formData.question, drawnCards.value, result.value)

  } catch (error) {
    console.error('解读失败:', error)
    if (error.message && error.message.includes('积分不足')) return
    ElMessage.error('解读失败，请稍后重试')
  } finally {
    isInterpreting.value = false
    stopLoadingMessages()
  }
}

const resetReading = () => {
  currentPhase.value = 'prepare'
  formData.question = ''
  formData.spread = 'single'
  result.value = ''
  drawnCards.value = []
  flippedCards.value = {}
  showCards.value = false
  mobileBannerCollapsed.value = false
}

// 洗牌动画样式
const getShuffleStyle = (index) => {
  const angles = [-30, -15, 0, 15, 30]
  const offsets = [-40, -20, 0, 20, 40]
  return {
    transform: `rotate(${angles[index - 1]}deg) translateX(${offsets[index - 1]}px)`,
    animationDelay: `${(index - 1) * 0.1}s`
  }
}

// 移动端自动滚动 - 使用 watch 监听状态变化，不侵入业务逻辑
const smoothScrollTo = (refEl) => {
  nextTick(() => {
    if (refEl.value) {
      refEl.value.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  })
}

// 检测牌面是否溢出容器，溢出时启用滑动模式
const checkCardsOverflow = () => {
  nextTick(() => {
    const container = mobileCardsRef.value?.querySelector('.mobile-cards-scroll')
    if (!container) return
    if (container.scrollWidth > container.clientWidth) {
      container.classList.add('has-overflow')
    } else {
      container.classList.remove('has-overflow')
    }
  })
}

watch(currentPhase, (val) => {
  if (!isMobile.value) return
  if (val === 'reading') {
    nextTick(() => smoothScrollTo(mobileQuestionRef))
  }
})

watch(drawnCards, (val) => {
  if (!isMobile.value || val.length === 0) return
  nextTick(() => {
    smoothScrollTo(mobileCardsRef)
    checkCardsOverflow()
  })
})

watch(allCardsFlipped, (val) => {
  if (!isMobile.value || !val) return
  nextTick(() => smoothScrollTo(mobileInterpretRef))
})

watch(result, (val) => {
  if (!isMobile.value || !val) return
  nextTick(() => smoothScrollTo(mobileResultRef))
})
</script>

<style scoped>

/* ============================================
   PC端样式（完整保留）
   ============================================ */

/* 页面头部 */
.page-header{display:flex;flex-direction:column;align-items:center;padding:12px;margin-bottom:8px;background:linear-gradient(135deg,var(--fh-accent-bg),rgba(251,146,60,0.1));border-radius:16px;border:1px solid var(--fh-accent-bg)}
.header-icon{font-size:48px;margin-bottom:10px;animation:float 3s ease-in-out infinite}
.subtitle{font-size:2rem;color:var(--fh-text-bright);margin:0 0 8px;text-shadow:0 0 10px var(--fh-shadow-color)}
.motto{font-size:1.1rem;color:var(--fh-accent-light);font-style:italic;margin:0}

@keyframes float{0%,100%{transform:translateY(0)}50%{transform:translateY(-10px)}}

/* 准备阶段 */
.prepare-section{margin-bottom:24px}
.guide-card{padding:0;overflow:hidden}
.guide-content{padding:30px;text-align:center}
.guide-content h3{color:var(--fh-accent-light);font-size:1.8rem;margin:0 0 30px}
.guide-steps{display:flex;flex-direction:column;gap:20px;margin-bottom:30px}
.guide-step{display:flex;align-items:flex-start;gap:16px;text-align:left;background:var(--fh-shadow-subtle);padding:16px;border-radius:12px}
.step-number{display:flex;align-items:center;justify-content:center;width:36px;height:36px;background:linear-gradient(135deg,var(--fh-accent-light),var(--fh-accent));color:var(--fh-text-bright);font-weight:bold;font-size:1.2rem;border-radius:50%;flex-shrink:0}
.step-content h4{color:var(--fh-text-bright);margin:0 0 6px;font-size:1.1rem}
.step-content p{color:var(--fh-text-muted);margin:0;font-size:0.95rem}
.start-btn{padding:16px 48px;font-size:1.1rem;border-radius:30px;background:linear-gradient(135deg,var(--fh-accent-light),var(--fh-accent));border:none;box-shadow:0 4px 20px var(--fh-shadow-color)}
.start-btn:hover{transform:translateY(-2px);box-shadow:0 6px 25px var(--fh-shadow-color)}

/* 输入区域 */
.input-section{margin-bottom:24px}
.card-header{background:linear-gradient(135deg,var(--fh-accent-light),var(--fh-accent));color:var(--fh-text-bright);padding:12px 20px;border-radius:12px 12px 0 0;font-weight:600}

/* 牌阵选择 */
.spread-section{margin-bottom:24px}
.spread-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(200px,1fr));gap:16px}
.spread-option{display:flex;align-items:center;gap:12px;padding:16px;background:var(--fh-shadow-medium);border:2px solid transparent;border-radius:12px;cursor:pointer;transition:all 0.3s}
.spread-option:hover:not(.disabled){border-color:var(--fh-shadow-color);background:var(--fh-accent-bg)}
.spread-option.active{border-color:var(--fh-accent-light);background:var(--fh-accent-bg)}
.spread-option.disabled{opacity:0.5;cursor:not-allowed}
.spread-icon{font-size:2rem}
.spread-info h4{color:var(--fh-text-bright);margin:0 0 4px;font-size:1rem}
.spread-info p{color:var(--fh-text-muted);margin:0 0 6px;font-size:0.85rem}
.card-count{font-size:0.75rem;color:var(--fh-accent-light);background:var(--fh-accent-bg);padding:2px 8px;border-radius:10px}

/* 抽牌按钮 */
.action-section{text-align:center;margin:30px 0}
.draw-btn{padding:16px 48px;font-size:1.1rem;border-radius:30px;background:linear-gradient(135deg,var(--fh-accent-light),var(--fh-accent));border:none;box-shadow:0 4px 20px var(--fh-shadow-color)}
.draw-btn:hover:not(:disabled){transform:translateY(-2px);box-shadow:0 6px 25px var(--fh-shadow-color)}

/* 洗牌动画 */
.shuffle-overlay{position:fixed;top:0;left:0;width:100%;height:100%;background:var(--fh-overlay);display:flex;align-items:center;justify-content:center;z-index:1000}
.shuffle-animation{text-align:center}
.shuffle-cards{display:flex;justify-content:center;gap:10px;margin-bottom:30px}
.shuffle-card{width:80px;height:120px;background:linear-gradient(135deg,var(--fh-surface-elevated),var(--fh-bg));border-radius:8px;border:2px solid var(--fh-accent-light);animation:shuffle 0.5s ease-in-out infinite alternate}
.shuffle-text{color:var(--fh-accent-light);font-size:1.2rem;animation:pulse 1.5s ease-in-out infinite}

@keyframes shuffle{0%{transform:translateY(0) rotate(0deg)}100%{transform:translateY(-20px) rotate(5deg)}}
@keyframes pulse{0%,100%{opacity:1}50%{opacity:0.5}}

/* 牌面展示 */
.cards-section{margin-bottom:24px}
.section-title{text-align:center;color:var(--fh-accent-light);font-size:1.3rem;margin-bottom:20px}
.cards-container{display:flex;justify-content:center;gap:20px;flex-wrap:wrap}
.cards-container.spread-single{justify-content:center}
.cards-container.spread-three{gap:30px}
.cards-container.spread-quarter{gap:25px}

.tarot-card{display:flex;flex-direction:column;align-items:center;gap:10px;opacity:0;transform:translateY(30px) rotateY(90deg);cursor:pointer}
.tarot-card.card-animate{animation:cardReveal 0.6s ease-out forwards}

/* 牌背 */
.card-back{position:relative;width:140px;height:220px;perspective:1000px}
.back-image{width:100%;height:100%;object-fit:cover;border-radius:12px;box-shadow:0 8px 24px var(--fh-overlay);border:2px solid rgba(212,175,55,0.5)}
.tap-hint{position:absolute;bottom:10px;left:50%;transform:translateX(-50%);background:var(--fh-overlay);color:var(--fh-accent-light);padding:4px 12px;border-radius:20px;font-size:0.75rem;opacity:0;transition:opacity 0.3s}
.tarot-card:hover .tap-hint{opacity:1}

/* 牌面 */
.card-front{width:140px}
.card-image-wrapper{position:relative;width:140px;height:220px;border-radius:12px;overflow:hidden;box-shadow:0 8px 24px var(--fh-overlay);border:2px solid rgba(212,175,55,0.5);transition:transform 0.3s,box-shadow 0.3s}
.card-image-wrapper:hover{transform:translateY(-8px);box-shadow:0 12px 32px var(--fh-shadow-color)}
.card-image{width:100%;height:100%;object-fit:cover;transition:transform 0.3s}
.card-image.reversed-img{transform:rotate(180deg)}
.reversed-badge{position:absolute;top:8px;right:8px;background:rgba(244,67,54,0.9);color:var(--fh-text-bright);font-size:0.75rem;padding:4px 8px;border-radius:4px}
.card-info{display:flex;flex-direction:column;align-items:center;gap:4px;padding:10px 14px;background:var(--fh-shadow-medium);border-radius:10px;min-width:120px;text-align:center}
.card-position{font-size:0.8rem;color:var(--fh-accent-light);font-weight:500}
.card-name{font-size:1rem;color:var(--fh-text-bright);font-weight:600}
.card-meaning{font-size:0.75rem;color:var(--fh-text-muted)}

/* 翻牌动画 */
.tarot-card.card-flipped .card-back{display:none}
.tarot-card:not(.card-flipped) .card-front{display:none}

@keyframes cardReveal{0%{opacity:0;transform:translateY(30px) rotateY(90deg)}50%{opacity:0.5;transform:translateY(15px) rotateY(45deg)}100%{opacity:1;transform:translateY(0) rotateY(0)}}

/* 翻牌按钮 */
.flip-all-btn{text-align:center;margin-top:20px}
.interpret-btn-section{text-align:center;margin:30px 0}
.loading-fun-msg{color:var(--fh-text-muted);font-size:0.85rem;margin-top:12px;animation:fadeInOut 2.5s ease-in-out infinite}
@keyframes fadeInOut{0%,100%{opacity:0.4}50%{opacity:1}}

/* 结果展示 */
.result-section{margin-top:24px}
.interpretation-header{display:flex;justify-content:space-between;align-items:center}
.spread-name{font-size:0.85rem;background:var(--fh-border);padding:4px 12px;border-radius:20px}
.interpretation{padding:20px;line-height:1.8;font-size:1rem;background:var(--fh-shadow-medium);border-radius:0 0 12px 12px;color:var(--fh-text)}
.interpretation :deep(h1),.interpretation :deep(h2),.interpretation :deep(h3){color:var(--fh-accent-light);margin-top:16px}
.interpretation :deep(p){margin:12px 0}
.interpretation :deep(ul),.interpretation :deep(ol){padding-left:24px;margin:12px 0}

/* 重新占卜 */
.restart-section{text-align:center;margin-top:30px}

/* PC端响应式（仅影响PC模板，移动端走独立模板） */
@media(max-width:768px){
  .cards-container{flex-direction:column;align-items:center}
  .card-back,.card-image-wrapper{width:110px;height:172px}
  .card-front{width:110px}
  .spread-grid{grid-template-columns:1fr}
  .guide-step{flex-direction:column;align-items:center;text-align:center}
}

@media(max-width:400px){
  .subtitle{font-size:1.4rem}
  .guide-content h3{font-size:1.3rem}
  .start-btn,.draw-btn{padding:14px 32px}
  .card-back{width:90px;height:140px}
}


/* ============================================
   移动端样式 (.mobile- 前缀 + var(--mobile-*))
   ============================================ */

.mobile-tarot {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  font-size: var(--mobile-font-base);
  color: var(--mobile-text-primary);
  padding: 0 var(--mobile-md);
  padding-bottom: calc(var(--mobile-xxl) + 60px);
}

/* -- 移动端头部 -- */
.mobile-header {
  display: flex;
  align-items: center;
  gap: var(--mobile-md);
  padding: var(--mobile-md);
  margin-bottom: var(--mobile-lg);
  background: var(--mobile-bg-card);
  border-radius: var(--mobile-radius-lg);
  box-shadow: var(--mobile-shadow-card);
}

.mobile-header-icon {
  font-size: 32px;
  flex-shrink: 0;
}

.mobile-header-text h2 {
  margin: 0;
  font-size: var(--mobile-font-xl);
  font-weight: 700;
  color: var(--mobile-text-primary);
}

.mobile-header-text p {
  margin: 2px 0 0;
  font-size: var(--mobile-font-sm);
  color: var(--mobile-text-hint);
  font-style: italic;
}

/* -- 准备Banner（可折叠） -- */
.mobile-prepare-banner {
  display: flex;
  flex-direction: column;
  gap: var(--mobile-lg);
}

.mobile-banner-card {
  background: var(--mobile-bg-card);
  border-radius: var(--mobile-radius-lg);
  box-shadow: var(--mobile-shadow-card);
  overflow: hidden;
  border: 1px solid var(--mobile-border-color);
}

.mobile-banner-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--mobile-md) var(--mobile-lg);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  user-select: none;
}

.mobile-banner-title {
  font-size: var(--mobile-font-lg);
  font-weight: 600;
  color: var(--mobile-text-primary);
}

.mobile-banner-toggle {
  font-size: var(--mobile-font-lg);
  color: var(--mobile-text-hint);
  transition: transform 0.2s;
}

.mobile-banner-body {
  padding: 0 var(--mobile-lg) var(--mobile-lg);
  display: flex;
  flex-direction: column;
  gap: var(--mobile-sm);
}

.mobile-banner-step {
  display: flex;
  align-items: center;
  gap: var(--mobile-md);
  padding: var(--mobile-sm) 0;
  font-size: var(--mobile-font-sm);
  color: var(--mobile-text-secondary);
  line-height: 1.4;
}

.mobile-step-num {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: var(--mobile-accent);
  color: #fff;
  font-size: var(--mobile-font-sm);
  font-weight: 700;
  flex-shrink: 0;
}

/* -- 通用按钮（全宽、48px最小高度、拇指可及） -- */
.mobile-action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 48px;
  padding: var(--mobile-md) var(--mobile-xl);
  border: none;
  border-radius: var(--mobile-radius-md);
  font-size: var(--mobile-font-lg);
  font-weight: 600;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  transition: opacity 0.15s, transform 0.1s;
}

.mobile-action-btn:active {
  transform: scale(0.97);
  opacity: 0.85;
}

.mobile-action-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  transform: none;
}

.mobile-start-btn,
.mobile-draw-btn,
.mobile-flip-all-btn,
.mobile-interpret-btn {
  background: var(--mobile-accent);
  color: #fff;
  box-shadow: 0 2px 8px rgba(230, 0, 35, 0.25);
}

.mobile-restart-btn {
  background: var(--mobile-bg-card);
  color: var(--mobile-text-primary);
  border: 1px solid var(--mobile-border-color);
}

/* -- 通用卡片 -- */
.mobile-card {
  background: var(--mobile-bg-card);
  border-radius: var(--mobile-radius-lg);
  box-shadow: var(--mobile-shadow-card);
  padding: var(--mobile-lg);
  margin-bottom: var(--mobile-lg);
  border: 1px solid var(--mobile-border-color);
}

.mobile-card-title {
  font-size: var(--mobile-font-lg);
  font-weight: 600;
  color: var(--mobile-text-primary);
  margin-bottom: var(--mobile-md);
}

/* -- Textarea -- */
.mobile-textarea {
  width: 100%;
  border: 1px solid var(--mobile-border-color);
  border-radius: var(--mobile-radius-md);
  padding: var(--mobile-md);
  font-size: var(--mobile-font-base);
  color: var(--mobile-text-primary);
  background: #fafafa;
  resize: none;
  line-height: 1.5;
  box-sizing: border-box;
  font-family: inherit;
  -webkit-appearance: none;
}

.mobile-textarea:focus {
  outline: none;
  border-color: var(--mobile-accent);
  background: #fff;
}

.mobile-textarea:disabled {
  opacity: 0.5;
  background: #f0f0f0;
}

/* -- 牌阵选择（水平滚动chip） -- */
.mobile-spread-label {
  font-size: var(--mobile-font-base);
  font-weight: 600;
  color: var(--mobile-text-primary);
  margin-top: var(--mobile-lg);
  margin-bottom: var(--mobile-sm);
}

.mobile-spread-chips {
  display: flex;
  gap: var(--mobile-sm);
  overflow-x: auto;
  padding-bottom: var(--mobile-sm);
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
}

.mobile-spread-chips::-webkit-scrollbar {
  display: none;
}

.mobile-spread-chip {
  display: flex;
  align-items: center;
  gap: var(--mobile-xs);
  flex-shrink: 0;
  padding: var(--mobile-sm) var(--mobile-md);
  border-radius: 20px;
  background: #f5f5f5;
  border: 1.5px solid transparent;
  font-size: var(--mobile-font-sm);
  color: var(--mobile-text-secondary);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  transition: all 0.15s;
  white-space: nowrap;
}

.mobile-spread-chip.mobile-chip-active {
  background: var(--mobile-accent-light);
  border-color: var(--mobile-accent);
  color: var(--mobile-accent);
  font-weight: 600;
}

.mobile-spread-chip.mobile-chip-disabled {
  opacity: 0.4;
  pointer-events: none;
}

.mobile-chip-icon {
  font-size: var(--mobile-font-base);
}

.mobile-chip-name {
  font-size: var(--mobile-font-sm);
}

.mobile-chip-count {
  font-size: 10px;
  color: var(--mobile-text-hint);
  background: rgba(0, 0, 0, 0.05);
  padding: 1px 6px;
  border-radius: 8px;
}

.mobile-spread-chip.mobile-chip-active .mobile-chip-count {
  background: rgba(230, 0, 35, 0.1);
  color: var(--mobile-accent);
}

/* -- 抽牌按钮在卡片内间距 -- */
.mobile-draw-btn {
  margin-top: var(--mobile-xl);
}

/* -- 洗牌动画（移动端） -- */
.mobile-shuffle-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.mobile-shuffle-animation {
  text-align: center;
}

.mobile-shuffle-cards {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-bottom: var(--mobile-xxl);
}

.mobile-shuffle-card {
  width: 60px;
  height: 90px;
  background: linear-gradient(135deg, #fff, var(--mobile-accent));
  border-radius: 6px;
  border: 2px solid rgba(230, 0, 35, 0.4);
  animation: mobile-shuffle 0.5s ease-in-out infinite alternate;
}

.mobile-shuffle-text {
  color: #fff;
  font-size: var(--mobile-font-lg);
  animation: mobile-pulse 1.5s ease-in-out infinite;
}

@keyframes mobile-shuffle {
  0% { transform: translateY(0) rotate(0deg); }
  100% { transform: translateY(-16px) rotate(5deg); }
}

@keyframes mobile-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

/* -- 牌面区域（横向滑动） -- */
.mobile-cards-section {
  margin-bottom: var(--mobile-lg);
}

.mobile-section-title {
  font-size: var(--mobile-font-lg);
  font-weight: 600;
  color: var(--mobile-text-primary);
  text-align: center;
  margin-bottom: var(--mobile-lg);
}

.mobile-cards-scroll {
  display: flex;
  justify-content: center;
  gap: var(--mobile-md);
  overflow-x: auto;
  padding: var(--mobile-sm) var(--mobile-lg) var(--mobile-lg);
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
}

/* 牌放不下时才允许滑动，否则居中 */
.mobile-cards-scroll::-webkit-scrollbar {
  display: none;
}

/* 内容溢出时左对齐+滑动 */
.mobile-cards-scroll.has-overflow {
  justify-content: flex-start;
  scroll-snap-type: x mandatory;
}

.mobile-cards-scroll.has-overflow .mobile-tarot-card {
  scroll-snap-align: center;
}

/* -- 移动端塔罗牌 -- */
.mobile-tarot-card {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--mobile-sm);
  opacity: 0;
  transform: translateY(20px) rotateY(90deg);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.mobile-tarot-card.mobile-card-animate {
  animation: mobile-cardReveal 0.6s ease-out forwards;
}

/* 牌背尺寸自适应屏幕 */
.mobile-card-back {
  position: relative;
  width: clamp(110px, 38vw, 160px);
  height: clamp(172px, 58vw, 250px);
}

.mobile-back-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: var(--mobile-radius-md);
  box-shadow: var(--mobile-shadow-elevated);
  border: 1.5px solid rgba(212, 175, 55, 0.4);
}

/* tap-hint 默认显示（移动端无hover） */
.mobile-tap-hint {
  position: absolute;
  bottom: 8px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.6);
  color: #fff;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 11px;
  opacity: 1;
  white-space: nowrap;
  pointer-events: none;
}

/* 牌面 */
.mobile-card-front {
  width: clamp(110px, 38vw, 160px);
}

.mobile-card-img-wrap {
  position: relative;
  width: clamp(110px, 38vw, 160px);
  height: clamp(172px, 58vw, 250px);
  border-radius: var(--mobile-radius-md);
  overflow: hidden;
  box-shadow: var(--mobile-shadow-elevated);
  border: 1.5px solid rgba(212, 175, 55, 0.4);
}

.mobile-card-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.mobile-card-img.mobile-img-reversed {
  transform: rotate(180deg);
}

.mobile-reversed-badge {
  position: absolute;
  top: 6px;
  right: 6px;
  background: var(--mobile-accent);
  color: #fff;
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 4px;
  font-weight: 600;
}

.mobile-card-meta {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: 6px 8px;
  background: #f5f5f5;
  border-radius: var(--mobile-radius-sm);
  text-align: center;
  min-width: clamp(100px, 36vw, 150px);
}

.mobile-card-position {
  font-size: 11px;
  color: var(--mobile-accent);
  font-weight: 500;
}

.mobile-card-name {
  font-size: var(--mobile-font-sm);
  color: var(--mobile-text-primary);
  font-weight: 600;
}

/* 翻牌显隐 */
.mobile-tarot-card.mobile-card-flipped .mobile-card-back { display: none; }
.mobile-tarot-card:not(.mobile-card-flipped) .mobile-card-front { display: none; }

@keyframes mobile-cardReveal {
  0% { opacity: 0; transform: translateY(20px) rotateY(90deg); }
  50% { opacity: 0.5; transform: translateY(10px) rotateY(45deg); }
  100% { opacity: 1; transform: translateY(0) rotateY(0); }
}

/* -- 翻开所有牌按钮 -- */
.mobile-flip-all-btn {
  margin-top: var(--mobile-md);
}

/* -- 解读结果出来后牌面区保留但弱化(可滑动对照) -- */
.mobile-cards-in-result {
  margin-top: var(--mobile-sm);
  opacity: 0.92;
}
.mobile-cards-in-result .mobile-section-title {
  font-size: 13px;
  margin-bottom: var(--mobile-sm);
}

/* -- 解读按钮区域 -- */
.mobile-action-section {
  margin: var(--mobile-xl) 0;
}

.mobile-loading-fun-msg {
  text-align: center;
  font-size: var(--mobile-font-sm);
  color: var(--mobile-text-hint);
  margin-top: var(--mobile-md);
  animation: mobile-fadeInOut 2.5s ease-in-out infinite;
}

@keyframes mobile-fadeInOut {
  0%, 100% { opacity: 0.4; }
  50% { opacity: 1; }
}

/* -- AI解读结果 -- */
.mobile-result-section {
  display: flex;
  flex-direction: column;
  gap: var(--mobile-lg);
}

.mobile-full-width {
  margin-bottom: 0;
}

.mobile-spread-badge {
  display: inline-block;
  font-size: 11px;
  color: var(--mobile-accent);
  background: var(--mobile-accent-light);
  padding: 2px 10px;
  border-radius: 12px;
  margin-left: var(--mobile-sm);
  font-weight: 500;
}

.mobile-interpretation {
  font-size: var(--mobile-font-base);
  line-height: 1.8;
  color: var(--mobile-text-primary);
}

.mobile-interpretation :deep(h1),
.mobile-interpretation :deep(h2),
.mobile-interpretation :deep(h3) {
  color: var(--mobile-accent);
  margin-top: var(--mobile-lg);
  margin-bottom: var(--mobile-sm);
  font-size: var(--mobile-font-lg);
}

.mobile-interpretation :deep(h1) {
  font-size: var(--mobile-font-xl);
}

.mobile-interpretation :deep(p) {
  margin: var(--mobile-sm) 0;
}

.mobile-interpretation :deep(ul),
.mobile-interpretation :deep(ol) {
  padding-left: var(--mobile-xl);
  margin: var(--mobile-sm) 0;
}

.mobile-interpretation :deep(li) {
  margin-bottom: var(--mobile-xs);
}

.mobile-interpretation :deep(strong) {
  color: var(--mobile-text-primary);
}

.mobile-interpretation :deep(blockquote) {
  border-left: 3px solid var(--mobile-accent);
  padding-left: var(--mobile-md);
  margin: var(--mobile-md) 0;
  color: var(--mobile-text-secondary);
}
</style>
